import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    // Dispatch event to signal that the dialog component is ready
    // This allows parent components to know when refs are initialized
    this.dispatchEvent(new CustomEvent('dialog-component:ready', { bubbles: true }));

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      document.addEventListener('click', this.#handleDocumentClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      if (!this.#shouldCloseOnEscapePress) {
        dialog.addEventListener('cancel', this.#handleDialogCancel);
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('click', this.#handleDocumentClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (!this.#shouldCloseOnEscapePress) {
      dialog.removeEventListener('cancel', this.#handleDialogCancel);
    }

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Checks if the dialog should close when clicking outside.
   *
   * @returns {boolean} True if the dialog should close on outside click.
   */
  get #shouldCloseOnOutsideClick() {
    const attr = this.getAttribute('dialog-close-on-outside-click');

    return attr !== 'false';
  }

  /**
   * Checks if the dialog should close when the user presses the escape key.
   *
   * @returns {boolean} True if the dialog should close on escape press.
   */
  get #shouldCloseOnEscapePress() {
    const attr = this.getAttribute('dialog-close-on-escape-press');

    return attr !== 'false';
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (!this.#shouldCloseOnOutsideClick) return;

    // If the click target has been removed from the DOM (e.g., during cart item deletion),
    // it can't be a legitimate "outside click" - ignore it
    if (event.target instanceof Element && !event.target.isConnected) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Handles document clicks to close the dialog when clicking anywhere outside.
   * Only triggers when the dialog is open.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (!this.#shouldCloseOnOutsideClick) return;

    // If the click target has been removed from the DOM (e.g., during cart item deletion),
    // it can't be a legitimate "outside click" - ignore it
    if (event.target instanceof Element && !event.target.isConnected) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    if (!this.#shouldCloseOnEscapePress) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Prevents the dialog from closing when the cancel event is triggered (e.g., by Escape key).
   * This is a more robust way to prevent native dialog closing behavior.
   *
   * @param {Event} event - The cancel event.
   */
  #handleDialogCancel = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}
